
import React from "react";
import PropTypes from "prop-types";
import { Card, Avatar, IconButton } from "@mui/material";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatNumber } from "utils/numberUtils";
import { formatTimeAgo } from "utils/datetimeUtils";
import facebookVerifiedIcon from "assets/images/icons/socials/facebook-verified.png";
import youtubeVerifiedIcon from "assets/images/icons/socials/youtube-verified.png";
import Tooltip from "@mui/material/Tooltip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import youtubeLogoDark from "assets/images/logos/youtube-logo-dark.png";
import HistoryIcon from '@mui/icons-material/History';



/**
 * SocialCard Component
 * @param {Object} props
 * @param {String} platform - Platform name (e.g., "Facebook", "YouTube", "Instagram")
 * @param {String} profilePic - Profile picture URL
 * @param {String} name - Profile name
 * @param {Number} count - Follower/Subscribers count
 * @param {Boolean} verifiedStatus - Whether the profile is verified
 * @param {Object} icons - Object containing platform and verified icons
 * @param {Boolean} isSelected - Whether the profile is selected
 * @param {Function} onSelect - Handler for selecting/deselecting the profile
 */
function SocialCard({
  platform,
  profilePic = "https://via.placeholder.com/150",
  name,
  count,
  link,
  verifiedStatus = false,
  updated_at,
  isSelected = false,
  onSelect = null,
  isOwn = false,
}) {

  // Calculate the time ago string using the imported utility
  const lastRefreshedDisplay = formatTimeAgo(updated_at);
  // const { verifiedIcon } = icons;
  const verifiedIcon = platform === "YouTube" ? youtubeVerifiedIcon : facebookVerifiedIcon;
  const countLabel = platform === "YouTube" ? "Subscribers" : "Followers";

  const renderPlatformIcon = () => {
    switch (platform) {
      case "Facebook":
        return <FacebookRoundedIcon sx={{ fontSize: 32 }} />;
      case "Instagram":
        return <InstagramIcon sx={{ fontSize: 32 }} />;
      case "YouTube":
        return (
          <MDBox
            component="img"
            src={youtubeLogoDark}
            alt="YouTube Logo"
            sx={{
              width: 20,
              height: 20,
              objectFit: "contain",
              display: "inline-block",
              mt: "2px", // Optional vertical alignment tweak
            }}
          />
        );
      default:
        return null;
    }
  };


  const handleClick = () => {
    if (onSelect) {
      onSelect(platform);
    }
  };

  const openLinkInNewTab = (e) => {
    e.stopPropagation();
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: 2,
        boxShadow: isSelected ? 6 : 1,
        border: isSelected ? "2px solid #00A1A1" : (isOwn ? "1px solid #b3e5fc" : "1px solid #ddd"), // Subtle blue border for own
        overflow: "hidden",
        position: "relative",
        width: "100%",
        maxWidth: 500,
        height: 80,
        // transition: "all 0.3s ease-in-out",
        // '&:hover': { boxShadow: 6 },
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: 6,
        },
        cursor: "pointer",
        backgroundColor: isSelected ? "grey.500" : (isOwn ? "#e6f2fb" : "grey.100"), // Very subtle blue tint for own
      }}
    >


      {/* 🟦 Profile Picture with Verification Badge */}
      <MDBox sx={{ position: "relative", marginRight: 2 }}>
        <Tooltip title={`${name}`} arrow>
          <Avatar
            src={profilePic || "https://via.placeholder.com/150"}
            sx={{
              width: 60,
              height: 60,
            }}
          />
        </Tooltip>
        {/* Verification Badge */}
        {verifiedStatus && verifiedIcon && (
          <Tooltip title="Verified Profile" arrow>
            <MDBox
              component="img"
              src={verifiedIcon}
              alt="Verified Badge"
              sx={{
                position: "absolute",
                bottom: 5,
                right: -8,
                width: 15,
                height: 15,
              }}
            />
          </Tooltip>
        )}
      </MDBox>

      {/* 🟩 Profile Details */}
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="flex-start"  // ⬅️ prevents horizontal centering
        textAlign="left"         // ⬅️ enforces left alignment for text
        flex={1}
        minWidth={0}             // ⬅️ CRITICAL: Allows flex child to shrink
      >
        <MDBox
          display="flex"
          flexDirection="row"
          alignItems="center"      // Changed to center for better alignment
          textAlign="left"
          ml="-10px"
          width="100%"             // Ensure row uses available width
        >
          {/* 🅟 Platform Icon (Top-Right Corner) */}
          <MDBox
            sx={{
              // color: "#8a8a8a",
              color: "#282828",
              transformOrigin: "center",
            }}
          >
            <Tooltip title={`${platform}`} arrow>
              {renderPlatformIcon()}
            </Tooltip>
          </MDBox>
          {/* Profile Name */}
          <Tooltip title={`${name}`} arrow placement="bottom-start">
            <MDTypography
              variant="h6"
              fontWeight="bold"
              sx={{
                fontSize: "0.95rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                minWidth: 0,
                display: "block",
                marginBottom: 0.5,
                ml: 0.5,
              }}
            >
              {name}
            </MDTypography>
          </Tooltip>
        </MDBox>
        <Tooltip title={`${count.toLocaleString()}`} arrow>
          {/* Metric Label and Count */}
          <MDTypography
            variant="body2"
            color="text"  // Keeping "text" but increasing opacity
            fontWeight="regular"
            opacity={0.9} // Increased from 0.6 for better visibility
            sx={{
              fontSize: "0.85rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mt: "-12px",
              ml: "15px",
            }}
          >
            {countLabel}: {formatNumber(count || 0)}
          </MDTypography>
        </Tooltip>
      </MDBox>

      {/* 🔗 Open in new tab icon */}
      <Tooltip title="Open Profile" arrow>
        <IconButton
          onClick={openLinkInNewTab}
          sx={{
            position: "absolute",
            top: 0,
            right: 1,
            color: "#8a8a8a",
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 15, width: 15, height: 15 }} />

        </IconButton>
      </Tooltip>

      {/* 🕒 Refreshed Time (NEW: BOTTOM-RIGHT Corner) */}
      {lastRefreshedDisplay && (
        <Tooltip title={`Refreshed ${lastRefreshedDisplay}`} arrow>
          <MDTypography
            variant="caption"
            color="text"
            opacity={0.9}
            sx={{
              position: "absolute",
              bottom: 4,
              right: 4, // ✅ keep it inside, not negative
              fontSize: "0.7rem",
              whiteSpace: "nowrap",
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              // backgroundColor: "rgba(255,255,255,0.8)", // optional for contrast
              borderRadius: "4px",
              px: 0.5, // ✅ adds small padding so text doesn’t touch edge
            }}
          >
            <HistoryIcon
              color="inherit"
              sx={{
                // Set icon size to match or slightly exceed font height for centering
                width: 14,
                height: 14,
                mr: 0.5, // Add spacing
                // Prevent the icon from inheriting unwanted font styles if needed
                color: "inherit"
              }}
            />
            {/* Text is now just the duration string */}
            {lastRefreshedDisplay}
          </MDTypography>
        </Tooltip>
      )}

      {/* ✅ Selection Tick Mark (Bottom-Right) */}
      {isSelected && (
        <MDBox
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: "#26282b", // Green tick background
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
          }}
        >
          <CheckCircleIcon color="accent" sx={{ fontSize: "20px !important" }} />

        </MDBox>
      )}
    </Card>
  );
}

// 🛡️ PropTypes Validation
SocialCard.propTypes = {
  platform: PropTypes.string.isRequired,
  profilePic: PropTypes.string,
  name: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  verifiedStatus: PropTypes.bool,
  icons: PropTypes.shape({
    platformIcon: PropTypes.string,
    verifiedIcon: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  isOwn: PropTypes.bool, // ✅ New Prop
};

export default SocialCard;
